import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdSearch, MdCheck, MdFileDownload, MdRefresh } from 'react-icons/md';
import '../master/MasterData.css';

interface BankTransaction {
  transactionId: number;
  transactionDate: string;
  description: string;
  referenceNo: string;
  debit: number;
  credit: number;
  balance: number;
  matched: boolean;
}

interface BookTransaction {
  transactionId: number;
  transactionDate: string;
  description: string;
  referenceNo: string;
  debit: number;
  credit: number;
  matched: boolean;
}

interface BankReconciliationData {
  reconciliationId: number;
  reconciliationNo: string;
  bankAccount: string;
  bankAccountNo: string;
  periodStart: string;
  periodEnd: string;
  bankStatementBalance: number;
  bookBalance: number;
  outstandingDeposits: number;
  outstandingChecks: number;
  reconciledBalance: number;
  difference: number;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'APPROVED';
  bankTransactions: BankTransaction[];
  bookTransactions: BookTransaction[];
}

const BankReconciliation: React.FC = () => {
  const [selectedAccount, setSelectedAccount] = useState<string>('BDO-001');
  const [reconciliationData, setReconciliationData] = useState<BankReconciliationData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // TODO: Fetch reconciliation data from API
    setReconciliationData({
      reconciliationId: 1,
      reconciliationNo: 'BR-2025-02-001',
      bankAccount: 'BDO Current Account',
      bankAccountNo: 'BDO-001-123456',
      periodStart: '2025-02-01',
      periodEnd: '2025-02-28',
      bankStatementBalance: 5250000,
      bookBalance: 5100000,
      outstandingDeposits: 200000,
      outstandingChecks: 350000,
      reconciledBalance: 5100000,
      difference: 0,
      status: 'IN_PROGRESS',
      bankTransactions: [
        {
          transactionId: 1,
          transactionDate: '2025-02-15',
          description: 'Customer payment - SM Furniture',
          referenceNo: 'DEP-20250215-001',
          debit: 450000,
          credit: 0,
          balance: 5450000,
          matched: true
        },
        {
          transactionId: 2,
          transactionDate: '2025-02-20',
          description: 'Vendor payment - Foam Suppliers',
          referenceNo: 'CHK-20250220-001',
          debit: 0,
          credit: 500000,
          balance: 4950000,
          matched: true
        },
        {
          transactionId: 3,
          transactionDate: '2025-02-28',
          description: 'Bank charges - Monthly fees',
          referenceNo: 'BCH-202502',
          debit: 0,
          credit: 1500,
          balance: 4948500,
          matched: false
        },
        {
          transactionId: 4,
          transactionDate: '2025-02-28',
          description: 'Customer payment - Home Depot',
          referenceNo: 'DEP-20250228-001',
          debit: 300000,
          credit: 0,
          balance: 5248500,
          matched: false
        }
      ],
      bookTransactions: [
        {
          transactionId: 1,
          transactionDate: '2025-02-15',
          description: 'Customer payment - SM Furniture',
          referenceNo: 'COL-2025-0001',
          debit: 450000,
          credit: 0,
          matched: true
        },
        {
          transactionId: 2,
          transactionDate: '2025-02-20',
          description: 'Vendor payment - Foam Suppliers',
          referenceNo: 'PAY-2025-0001',
          debit: 0,
          credit: 500000,
          matched: true
        },
        {
          transactionId: 3,
          transactionDate: '2025-02-25',
          description: 'Customer payment - Abenson',
          referenceNo: 'COL-2025-0002',
          debit: 200000,
          credit: 0,
          matched: false
        },
        {
          transactionId: 4,
          transactionDate: '2025-02-27',
          description: 'Vendor payment - Chemical Trading',
          referenceNo: 'PAY-2025-0002',
          debit: 0,
          credit: 350000,
          matched: false
        }
      ]
    });
  }, [selectedAccount]);

  const handleMatchTransaction = (transactionId: number, type: 'bank' | 'book') => {
    alert(`Match transaction functionality would be implemented here`);
    // TODO: Implement transaction matching logic
  };

  const handleCompleteReconciliation = () => {
    if (reconciliationData?.difference === 0) {
      if (window.confirm('Complete this bank reconciliation?')) {
        alert('Bank reconciliation completed');
        // TODO: Update status to COMPLETED
      }
    } else {
      alert('Cannot complete reconciliation. There is a difference of ₱' +
        reconciliationData?.difference.toLocaleString());
    }
  };

  const handleExport = () => {
    alert('Export reconciliation report functionality would be implemented here');
  };

  const handleRefresh = () => {
    alert('Refreshing bank transactions...');
    // TODO: Refresh from API
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Bank Reconciliation</h2>
            <p className="subtitle">Match bank statement with book transactions</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-secondary" onClick={handleRefresh}>
              <MdRefresh /> Refresh
            </button>
            <button className="btn btn-secondary" onClick={handleExport}>
              <MdFileDownload /> Export
            </button>
            {reconciliationData?.status === 'IN_PROGRESS' && (
              <button className="btn btn-primary" onClick={handleCompleteReconciliation}>
                <MdCheck /> Complete Reconciliation
              </button>
            )}
          </div>
        </div>

        {reconciliationData && (
          <>
            <div className="summary-cards">
              <div className="summary-card">
                <div className="summary-icon">🏦</div>
                <div className="summary-content">
                  <p className="summary-label">Bank Statement Balance</p>
                  <p className="summary-value">₱{reconciliationData.bankStatementBalance.toLocaleString()}</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">📖</div>
                <div className="summary-content">
                  <p className="summary-label">Book Balance</p>
                  <p className="summary-value">₱{reconciliationData.bookBalance.toLocaleString()}</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">
                  {reconciliationData.difference === 0 ? '✅' : '⚠️'}
                </div>
                <div className="summary-content">
                  <p className="summary-label">Difference</p>
                  <p className="summary-value" style={{
                    color: reconciliationData.difference === 0 ? '#28a745' : '#dc3545'
                  }}>
                    ₱{reconciliationData.difference.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="page-content" style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1a237e' }}>Bank Statement Transactions</h3>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Reference No</th>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th>Balance</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconciliationData.bankTransactions.map(transaction => (
                      <tr key={transaction.transactionId}>
                        <td>{new Date(transaction.transactionDate).toLocaleDateString('en-US')}</td>
                        <td>{transaction.description}</td>
                        <td className="code">{transaction.referenceNo}</td>
                        <td className="amount">
                          {transaction.debit > 0 ? `₱${transaction.debit.toLocaleString()}` : '-'}
                        </td>
                        <td className="amount">
                          {transaction.credit > 0 ? `₱${transaction.credit.toLocaleString()}` : '-'}
                        </td>
                        <td className="amount">₱{transaction.balance.toLocaleString()}</td>
                        <td>
                          {transaction.matched ? (
                            <span className="badge badge-success">MATCHED</span>
                          ) : (
                            <span className="badge badge-warning">UNMATCHED</span>
                          )}
                        </td>
                        <td>
                          {!transaction.matched && (
                            <button
                              className="btn-icon"
                              onClick={() => handleMatchTransaction(transaction.transactionId, 'bank')}
                              title="Match Transaction"
                              style={{ color: '#28a745' }}
                            >
                              <MdCheck />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="page-content">
              <h3 style={{ marginBottom: '15px', color: '#1a237e' }}>Book Transactions</h3>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Reference No</th>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconciliationData.bookTransactions.map(transaction => (
                      <tr key={transaction.transactionId}>
                        <td>{new Date(transaction.transactionDate).toLocaleDateString('en-US')}</td>
                        <td>{transaction.description}</td>
                        <td className="code">{transaction.referenceNo}</td>
                        <td className="amount">
                          {transaction.debit > 0 ? `₱${transaction.debit.toLocaleString()}` : '-'}
                        </td>
                        <td className="amount">
                          {transaction.credit > 0 ? `₱${transaction.credit.toLocaleString()}` : '-'}
                        </td>
                        <td>
                          {transaction.matched ? (
                            <span className="badge badge-success">MATCHED</span>
                          ) : (
                            <span className="badge badge-warning">UNMATCHED</span>
                          )}
                        </td>
                        <td>
                          {!transaction.matched && (
                            <button
                              className="btn-icon"
                              onClick={() => handleMatchTransaction(transaction.transactionId, 'book')}
                              title="Match Transaction"
                              style={{ color: '#28a745' }}
                            >
                              <MdCheck />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="page-content" style={{ marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1a237e' }}>Reconciliation Summary</h3>
              <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                      Bank Statement Balance
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                      ₱{reconciliationData.bankStatementBalance.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                      Outstanding Deposits (+)
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                      ₱{reconciliationData.outstandingDeposits.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                      Outstanding Checks (-)
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc3545' }}>
                      ₱{reconciliationData.outstandingChecks.toLocaleString()}
                    </div>
                  </div>
                  <div style={{ gridColumn: '1 / -1', borderTop: '2px solid #ddd', paddingTop: '15px', marginTop: '10px' }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                      Reconciled Book Balance
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1a237e' }}>
                      ₱{reconciliationData.reconciledBalance.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default BankReconciliation;
